/**
 * Modal "Asignación directa" — Sprint 17 S17-13b.
 *
 * Vía de excepción D-S17-11: el tesorero registra una asignación fuera
 * del flujo de solicitud del vecino (acuerdo verbal con comisión).
 *
 * Form: tipo + lote + residente (de la casa) + vehículo (si tipo=CR) +
 * monto_a_cobrar (default desde valor_venta del stock próximo) +
 * justificación obligatoria.
 *
 * Submit → POST /recursos/asignacion-directa. Backend crea IngresoVario
 * en `por_cobrar` + SolicitudRecurso sintética en `esperando_cobro`.
 * Al clasificar el pago de ese IV, el flujo de stock dispara la
 * asignación automáticamente (S17-06).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { crearAsignacionDirecta } from '../../lib/api/stock-recursos';
import {
  listarResidentesDeLote,
  type ResidenteConPersonaRead,
} from '../../lib/api/residentes';
import {
  listarRecursosDeLote,
  listarVehiculosDeLote,
} from '../../lib/api/lotes';
import {
  residentesConLlaveroVivo,
  vehiculosConCrVivo,
} from '../../lib/recursos';
import type {
  LoteResumen,
  RecursoConSuspensionRead,
  StockRecursoDisponibleResumen,
  TipoAsignacionDirecta,
  VehiculoRead,
} from '../../lib/api/types';
import { Button } from '../ui/Button';
import { InputMontoCLP } from '../ui/InputMontoCLP';
import { hoy } from '../../lib/tiempo';

interface Props {
  lotes: LoteResumen[];
  stockResumen: StockRecursoDisponibleResumen[];
  onCerrar: () => void;
  onCreada: (tipo: TipoAsignacionDirecta, monto: number) => void;
}

const TIPOS: { value: TipoAsignacionDirecta; label: string }[] = [
  { value: 'llavero', label: 'Llavero' },
  { value: 'control_remoto', label: 'Control remoto' },
];

export function ModalAsignacionDirecta({
  lotes,
  stockResumen,
  onCerrar,
  onCreada,
}: Props) {
  const [tipo, setTipo] = useState<TipoAsignacionDirecta>('llavero');
  const [loteId, setLoteId] = useState('');
  const [residenteId, setResidenteId] = useState('');
  const [vehiculoId, setVehiculoId] = useState('');
  // Sprint 17 S17-Z13: state numérico con InputMontoCLP.
  const [montoStr, setMontoStr] = useState<number | null>(null);
  const [justificacion, setJustificacion] = useState('');

  const [residentes, setResidentes] = useState<ResidenteConPersonaRead[]>([]);
  const [vehiculos, setVehiculos] = useState<VehiculoRead[]>([]);
  // Sprint 22: recursos de la casa, para descontar a quien ya tiene.
  const [recursos, setRecursos] = useState<RecursoConSuspensionRead[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monto = montoStr ?? 0;

  // Carga residentes + vehículos + recursos cuando cambia el lote.
  useEffect(() => {
    if (!loteId) {
      setResidentes([]);
      setVehiculos([]);
      setRecursos([]);
      setResidenteId('');
      setVehiculoId('');
      return;
    }
    let cancelado = false;
    Promise.all([
      listarResidentesDeLote(loteId),
      listarVehiculosDeLote(loteId),
      listarRecursosDeLote(loteId),
    ])
      .then(([rs, vs, recs]) => {
        if (cancelado) return;
        // Solo residentes aprobados + activos (sin hasta o hasta futuro).
        const fechaHoy = hoy();
        const aprobados = rs.filter(
          (r) =>
            r.estado === 'aprobado' && (r.hasta === null || r.hasta >= fechaHoy),
        );
        setResidentes(aprobados);
        setRecursos(recs);
        // Sprint 22: el autoselect solo aplica si la única persona de la casa
        // está realmente disponible. Antes preseleccionaba al titular incluso
        // si ya tenía llavero — el atajo que llenó de llaveros a la casa 6975.
        const sinLlavero = residentesConLlaveroVivo(recs);
        const candidatos = aprobados.filter((r) => !sinLlavero.has(r.id));
        if (candidatos.length === 1) setResidenteId(candidatos[0]!.id);
        // Solo vehículos activos.
        const activos = vs.filter((v) => v.activo);
        setVehiculos(activos);
      })
      .catch(() => {
        if (cancelado) return;
        setResidentes([]);
        setVehiculos([]);
        setRecursos([]);
      });
    return () => {
      cancelado = true;
    };
  }, [loteId]);

  // Default del monto cuando cambia el tipo: valor_venta del próximo FIFO.
  useEffect(() => {
    const r = stockResumen.find((x) => x.tipo === tipo);
    if (r?.valor_venta_proximo !== null && r?.valor_venta_proximo !== undefined) {
      setMontoStr(r.valor_venta_proximo);
    }
  }, [tipo, stockResumen]);

  // Sprint 22 — candidatos disponibles. Asimetría deliberada: para un
  // LLAVERO el recurso escaso es la persona; para un CONTROL es el auto (una
  // persona con dos autos lleva dos controles).
  const residentesDisponibles = (() => {
    if (tipo !== 'llavero') return residentes;
    const conLlavero = residentesConLlaveroVivo(recursos);
    return residentes.filter((r) => !conLlavero.has(r.id));
  })();
  const vehiculosDisponibles = (() => {
    const conControl = vehiculosConCrVivo(recursos);
    return vehiculos.filter((v) => !conControl.has(v.id));
  })();

  const cantidadDisponible = (() => {
    const r = stockResumen.find((x) => x.tipo === tipo);
    return r?.cantidad_disponible ?? 0;
  })();

  const formValido =
    loteId !== '' &&
    residenteId !== '' &&
    (tipo !== 'control_remoto' || vehiculoId !== '') &&
    monto > 0 &&
    justificacion.trim().length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!formValido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await crearAsignacionDirecta({
        tipo,
        lote_id: loteId,
        residente_id: residenteId,
        vehiculo_id: tipo === 'control_remoto' ? vehiculoId : null,
        monto_a_cobrar: monto,
        justificacion: justificacion.trim(),
      });
      onCreada(tipo, monto);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Asignación directa"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Asignación directa
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="bg-orange/[0.08] border-l-4 border-orange p-3 rounded-sm text-xs text-text-secondary">
          Vía de excepción para asignar un recurso fuera del flujo de
          solicitud del vecino (acuerdo verbal con comisión). Se crea un
          ingreso varios en estado <strong>por cobrar</strong>; cuando se
          clasifica el pago en banco, la asignación al stock se dispara
          automáticamente. Para cobros que pasaron por la cola del vecino,
          usar el flujo normal — no este.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Tipo de recurso</span>
            <select
              value={tipo}
              onChange={(e) =>
                setTipo(e.target.value as TipoAsignacionDirecta)
              }
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            >
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <span
              className={`block text-[11px] ${
                cantidadDisponible === 0
                  ? 'text-state-danger'
                  : 'text-text-muted'
              }`}
            >
              Stock disponible: {cantidadDisponible}
            </span>
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Casa</span>
            <select
              value={loteId}
              onChange={(e) => setLoteId(e.target.value)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            >
              <option value="">— elige —</option>
              {lotes.map((l) => (
                <option key={l.id} value={l.id}>
                  Casa {l.identificador}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Residente</span>
          <select
            value={residenteId}
            onChange={(e) => setResidenteId(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary disabled:opacity-50"
            required
            disabled={!loteId}
          >
            <option value="">
              {loteId ? '— elige —' : 'elige una casa primero'}
            </option>
            {residentesDisponibles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.persona_nombre_completo}
                {r.parentesco !== 'titular' && ` (${r.parentesco})`}
              </option>
            ))}
          </select>
          {loteId && residentes.length === 0 && (
            <span className="text-[11px] text-state-warning">
              Sin residentes aprobados activos en esta casa.
            </span>
          )}
          {loteId && residentes.length > 0 && residentesDisponibles.length === 0 && (
            <span className="text-[11px] text-state-warning">
              Todas las personas registradas en esta casa ya tienen llavero
              (cada persona lleva uno). Si el llavero es para alguien más, esa
              persona tiene que estar registrada como residente; si es un
              reemplazo, primero hay que dar de baja el anterior.
            </span>
          )}
        </label>

        {tipo === 'control_remoto' && (
          <label className="text-sm space-y-1 block">
            <span className="text-text-secondary">Vehículo</span>
            <select
              value={vehiculoId}
              onChange={(e) => setVehiculoId(e.target.value)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary disabled:opacity-50"
              required
              disabled={!loteId}
            >
              <option value="">
                {loteId ? '— elige —' : 'elige una casa primero'}
              </option>
              {vehiculosDisponibles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.patente}
                  {v.marca && ` · ${v.marca}`}
                  {v.modelo && ` ${v.modelo}`}
                </option>
              ))}
            </select>
            {loteId && vehiculos.length === 0 && (
              <span className="text-[11px] text-state-warning">
                Sin vehículos activos en esta casa.
              </span>
            )}
            {loteId && vehiculos.length > 0 && vehiculosDisponibles.length === 0 && (
              <span className="text-[11px] text-state-warning">
                Todos los vehículos de esta casa ya tienen control remoto (cada
                auto lleva uno). Si es un reemplazo, primero hay que dar de baja
                el anterior.
              </span>
            )}
          </label>
        )}

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Monto a cobrar (CLP)</span>
          <InputMontoCLP
            value={montoStr}
            onChange={setMontoStr}
            placeholder="0"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
          <span className="block text-[11px] text-text-muted">
            Default: valor de venta del próximo item de stock. Ajusta si
            corresponde otro acuerdo.
          </span>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Justificación <span className="text-state-danger">*</span>
          </span>
          <textarea
            value={justificacion}
            onChange={(e) => setJustificacion(e.target.value)}
            rows={3}
            placeholder="Ej: Acordado en reunión de comisión del 2026-05-20: 3er CR para casa 6951 por necesidad operativa."
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
            minLength={1}
          />
          <span className="block text-[11px] text-text-muted">
            Queda registrada en el ingreso varios y la solicitud sintética.
          </span>
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!formValido || submitting}
          >
            {submitting ? 'Registrando…' : 'Registrar asignación'}
          </Button>
        </div>
      </form>
    </div>
  );
}
